// The CASINO panel (casino.md, ECONOMY · CASINO).
//
// The panel owns ONE host element, the `<div class="casino-host">` that
// `casinoTab()` returns. The tab body around it is a constant string, so a
// table frame at four frames a second never churns the overlay's
// `lastBody` guard and never rebuilds the dock under the player's finger.
// This class carries the change guard for its own half: `sync` builds the
// markup, compares it with `lastHtml` and writes the DOM only on a change
// (ui-map-interface.md §10.3).
//
// **Every control acts on the RELEASE.** The host sits inside the dock's
// tab body, which pans under the same finger, so the press that starts a
// scroll must not buy a seat. The body already carries one `ListTap`
// recogniser (R578) and it reaches every button with a `data-act`; a
// casino button therefore carries `data-act="casino"` plus its own
// `data-op`, and `Overlay.actButton` hands the release to `act` below.
// Nothing here adds a listener of its own.
//
// **No sentence is a literal.** Every word comes from `gen/strings.ts`,
// and every figure a sentence states comes from the hello catalog's
// `CasinoRules` (R355), never from a number written here.
//
// **An armed casino button must carry its own `data-id`.** `Overlay.armKey`
// builds the gate key from `data-act` and `data-id`, so two armed casino
// buttons with no id would share one key, and a press on the first would
// leave the second armed. LEAVE TABLE takes the table's id, LEAVE QUEUE
// takes 0, and a game screen that arms a control must give it an id no
// other control of that screen uses.
//
// **The screen a game draws.** The four game screens are `GameView`
// implementations (`gameview.ts`). The panel names each one against its
// `CasinoGame`, and it picks one — the game of the table the reader sits
// at, or the slot machine the reader opened from the lobby.
// A screen returns the whole markup as one string, and the convention is
// two parts: `<div class="casino-felt">…</div>` holds what the reader
// looks at and scrolls, and `<div class="casino-bar">…</div>` holds the
// controls. The sheet makes the second part sticky at the bottom of the
// tab body, so the bar stays under the thumb while the felt scrolls.
//
// **The wide pane** (R2198, ui-map-interface.md §12.4). On a desktop under
// the desktop casino setting UNDOCKED the host stands in the casino pane
// over the map, and the panel draws two regions side by side: the board
// (the lobby) and the table (the screen of the game on the display). Each
// region keeps its own guard and its own parts, so a lobby frame never
// rewrites the table and never stops the wheel or the reels.

import type { GameState } from '../../state';
import type { UIActions } from '../overlay';
import type { ArmGate } from '../arm';
import type { MapKeyId } from '../../gen/presentation';
import {
  CASINO_BALL_MS, CASINO_FLASH_MS, CASINO_REEL_MS, CASINO_REEL_STAGGER_MS, CASINO_TIMER_FADE_MS,
  CASINO_TIMER_WARN_FRAC, CASINO_WHEEL_MS,
} from '../../gen/presentation';
import {
  CasinoGame, CasinoPhase, type CasinoLobby, type CasinoTable,
} from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { reducedMotion } from '../../render/motion';
import { armFaces } from '../choice/choice';
import { esc } from '../esc';
import { blackjackView } from './blackjack';
import { armedCtl, ctl } from './cards';
import { casinoHandKey, casinoIsSeated, casinoOwnRows } from './casinostate';
import { part, splitParts, type GameCtx, type GameView, type ScreenParts } from './gameview';
import { helpFacts, helpKey, helpPart } from './help';
import { clubOf, clubStyle } from './club';
import { lobbyHtml, queuedFor } from './lobby';
import { visitLine } from './moment';
import { pokerView } from './poker';
import { hasLanded } from './landing';
import { rouletteView } from './roulette';
import { slotsView } from './slots';
import { chromeRect } from '../uiscale';
import { readStore, writeStore } from '../localstore';

/**
 * The store key of the reader's stake tier (R2143). The pick is a
 * per-browser convenience: a store that refuses gives the lowest tier, as
 * a first visit does.
 */
export const TIER_KEY = 'ap.casino.tier';

/** The two tokens of the club ink the host carries (R2165). */
const CLUB_TOKENS = ['--club', '--club-rgb'] as const;

/**
 * The game screens, one a game, named HERE and not by themselves (R988).
 *
 * A module that registered itself at import time would only reach a
 * registry while somebody imports it, and a tree shaker drops an import
 * whose exports nobody reads. So the panel names its four screens, the
 * four screens stay pure modules, and a test reads any one of them
 * without the panel. The panel reads the table and knows nothing else
 * about them.
 */
const VIEWS: Partial<Record<CasinoGame, GameView>> = {
  [CasinoGame.CASINO_GAME_ROULETTE]: rouletteView,
  [CasinoGame.CASINO_GAME_BLACKJACK]: blackjackView,
  [CasinoGame.CASINO_GAME_SLOTS]: slotsView,
  [CasinoGame.CASINO_GAME_HOLDEM]: pokerView,
};

/** The screen of one game, or undefined while none draws it. */
export function gameView(game: CasinoGame): GameView | undefined {
  return VIEWS[game];
}

/**
 * One region the panel writes: the whole host in the dock column and in the
 * sheet, or one of the two columns of the wide pane (R2198).
 */
interface Region {
  el: HTMLElement;
  /** The markup the region holds now. The guard of §10.3. */
  html: string;
  /**
   * The parts the region holds now, or null while the markup carries none.
   *
   * The panel patches PER PART (`gameview.ts`): a countdown that steps
   * four times a second rewrites its own small part and leaves the wheel
   * and the reels where they stand, so an animation runs to its end
   * (R988).
   */
  parts: ScreenParts | null;
}

export class CasinoPanel {
  /**
   * The element the panel draws into.
   *
   * The guard above is a string compare, and a string compare alone is
   * wrong the moment the dock rebuilds its body: the host is then a NEW
   * and empty element, and the unchanged markup would never be written
   * into it. So the element is held as well, and a host this panel has
   * not drawn into is always written.
   */
  private host: HTMLElement | null = null;
  /** True while the host stands in the wide pane (R2198). */
  private wide = false;
  /** The regions of the host: one, or the board and the table of the wide
   *  pane. Each holds the guard of its own markup. */
  private regions: Region[] = [];
  /** The table and hand the panel last brought the wheel into view for,
   *  so one spin scrolls one time (R1210). */
  private spunKey = '';
  /** True from a QUICK JOIN press until the queue card it asked for is in
   *  view, so one press scrolls one time. */
  private queueFollow = false;
  /**
   * The stake tier this reader picked, a `CasinoStake.tier` that
   * `stakeAt` finds the row of, or null while the reader picked none.
   *
   * The pick is the reader's intent before the wire sees it, so it lives
   * here and never on the wire. ONE tier serves the four games, because
   * the lobby states the stake once (R2143), and the browser store keeps
   * it across visits and reloads. `pickTier` of `lobby.ts` takes a tier
   * the reader can no longer pay down to the highest one the reader can.
   */
  private tier: number | null = readTier();
  /** The club faction whose ink the host wears, or -1 before the first
   *  write (R2165). */
  private club = -1;
  /** The help fold of each game the reader opened (R2143), read from the
   *  store once a game, so the 4 Hz render reads no store. */
  private readonly help = new Map<CasinoGame, boolean>();
  /**
   * The slot machine the reader stands at, or null.
   *
   * The machine is the one game with no table and no seat: a pull settles
   * at once (casino.md §4.3), so the server has nothing to put in a
   * `CasinoTable` frame. The lobby's own button therefore opens the screen
   * here, and LEAVE closes it again.
   */
  private machine: CasinoGame | null = null;
  /**
   * The readings at the start of this casino visit, or null while the
   * reader is not in the casino (R2115). A visit runs from the first
   * render of the CASINO tab to the `reset` that closes it; the visit net
   * is the difference of `CasinoSlice.netSum` against this reading.
   */
  private visit: { net: number; played: number; seq: number } | null = null;
  /** The result counter of the last result the panel drew, and the
   *  `performance.now()` of that first render (`GameCtx.resultAtMs`). */
  private resultSeq = 0;
  private resultAt = 0;
  /** The highest result counter whose win flash played, and whose
   *  sentence the live region said. A result plays and speaks once. */
  private flashDone = 0;
  private sayDone = 0;
  /** The key of the last screen cue the live region said (R2142). */
  private cueDone = '';
  /**
   * The one polite live region of the casino (R2115). It stands in the
   * host as its last child and outside every part, because a part the
   * panel writes again is a new element and a new region is not said.
   */
  private live: HTMLElement | null = null;

  constructor(
    private readonly state: GameState,
    /** The wire. The lobby and the table controls send through it. */
    private readonly actions: UIActions,
    /**
     * The chrome's two-press gate (R696). LEAVE TABLE gives up a seat
     * with chips on it, so it is an armed control like every other
     * destructive one.
     */
    private readonly gate: ArmGate,
  ) {}

  /**
   * Draws the panel into the host, and does nothing at all while the
   * markup has not changed.
   *
   * The overlay calls this after the tab-body swap, and only while the
   * CASINO tab is the open one: a closed tab holds no host, and a panel
   * that drew into a detached element would pay for a picture nobody
   * sees.
   */
  sync(host: HTMLElement, wide = false): void {
    // A fresh host: the tab body was rebuilt and threw the old one away,
    // or the casino moved between the dock and the pane (R2198).
    // The three animation clocks are written once, here, because the
    // stylesheet must state no duration of its own — the figures belong
    // to `data/presentation/presentation.toml` and reach the sheet as
    // custom properties. It is six writes a tab opening and none a frame.
    const fresh = host !== this.host || wide !== this.wide;
    if (fresh) {
      this.host = host;
      this.wide = wide;
      this.writeClocks(host);
      this.regions = wide ? this.paneRegions(host) : [{ el: host, html: '', parts: null }];
      // A fresh host writes the reels again, and they land again, so the
      // verdict waits for that landing too (R2115).
      this.resultAt = performance.now();
    }
    this.wearClub(host, fresh);
    this.noteResult();
    const htmls = wide ? this.paneBody() : [this.body()];
    let wrote = false;
    for (let i = 0; i < htmls.length; i++) {
      const r = this.regions[i];
      if (!fresh && htmls[i] === r.html) continue;
      // The reader's motion answer is one class on the host, and the sheet
      // holds the twin of every animation behind it (R541). It goes on
      // with the markup, so it costs nothing on a frame that writes
      // nothing.
      if (!wrote) host.classList.toggle('still', reducedMotion());
      wrote = true;
      this.writeRegion(r, htmls[i], fresh);
    }
    if (!wrote) return;
    this.moment(host);
    this.followSpin(host);
    this.followQueue();
  }

  /**
   * Writes one region. One shape and one region: the panel writes the
   * parts that moved and leaves every other element where it stands.
   */
  private writeRegion(r: Region, html: string, fresh: boolean): void {
    r.html = html;
    const next = splitParts(html);
    if (!fresh && next && r.parts && next.shape === r.parts.shape && this.patch(r.el, r.parts, next)) {
      r.parts = next;
      return;
    }
    this.writeAll(r.el, html);
    r.parts = next;
  }

  /**
   * The two regions of the wide pane (R2198): the board and the table, as
   * two columns of the host, with the live region after them. Each column
   * scrolls on its own, so a reader who scrolls the board keeps the table.
   */
  private paneRegions(host: HTMLElement): Region[] {
    const live = this.liveRegion();
    removeAllBut(host, live);
    const board = document.createElement('div');
    board.className = 'cw-board';
    const table = document.createElement('div');
    table.className = 'cw-table';
    host.append(board, table, live);
    return [{ el: board, html: '', parts: null }, { el: table, html: '', parts: null }];
  }

  /**
   * Writes the whole markup and keeps the live region. The region is the
   * one node the write leaves in place, so a screen reader keeps it
   * through a change of the screen's shape.
   */
  private writeAll(el: HTMLElement, html: string): void {
    const live = this.liveRegion();
    removeAllBut(el, live);
    el.insertAdjacentHTML('afterbegin', html);
    // A column of the wide pane holds no live region: it stands in the
    // host beside the two columns.
    if (el === this.host && live.parentNode !== el) el.appendChild(live);
  }

  /** The live region, made on the first write. */
  private liveRegion(): HTMLElement {
    if (!this.live) {
      const el = document.createElement('div');
      el.className = 'casino-live';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('aria-atomic', 'true');
      this.live = el;
    }
    return this.live;
  }

  /**
   * Stamps a new result, and opens the visit on the first render after a
   * `reset`. A result that stood before the visit opened plays no flash
   * and says nothing: the reader did not see it land.
   */
  private noteResult(): void {
    const seq = this.state.casino.resultSeq;
    if (seq !== this.resultSeq) {
      this.resultSeq = seq;
      this.resultAt = performance.now();
    }
    if (this.visit) return;
    const s = this.state.casino;
    this.visit = { net: s.netSum, played: s.played, seq };
    this.flashDone = Math.max(this.flashDone, seq);
    this.sayDone = Math.max(this.sayDone, seq);
  }

  /**
   * The win flash and the speech of a result that the screen now shows
   * (`moment.ts`). An element marked with a result counter that has not
   * played takes the `flash` class one time; under reduced motion it takes
   * none, and the highlight stands at once. The result line's sentence
   * goes into the live region one time a result.
   */
  private moment(host: HTMLElement): void {
    let top = this.flashDone;
    const still = reducedMotion();
    for (const el of host.querySelectorAll<HTMLElement>('[data-flash]')) {
      const k = Number(el.dataset.flash ?? 0);
      if (k <= this.flashDone) continue;
      if (!still) el.classList.add('flash');
      top = Math.max(top, k);
    }
    this.flashDone = top;
    const say = host.querySelector<HTMLElement>('[data-say]');
    const k = Number(say?.dataset.say ?? 0);
    if (say && k > this.sayDone) {
      this.sayDone = k;
      this.liveRegion().textContent = say.dataset.speech ?? '';
    }
    // A screen's own cue (R2142): a line it says once a key, such as the
    // start of the reader's hold'em turn, its last quarter and the
    // timeout fold. The screen builds the key; a new key speaks.
    const cue = host.querySelector<HTMLElement>('[data-cue]');
    const ck = cue?.dataset.cue ?? '';
    if (cue && ck !== '' && ck !== this.cueDone) {
      this.cueDone = ck;
      this.liveRegion().textContent = cue.dataset.speech ?? '';
    }
  }

  /**
   * The net of this visit and the hands it counts, from the slice's sums
   * against the visit's start (R2115). A result whose picture still moves
   * is left out, so the figure beside LEAVE never gives a spin away
   * before the ball is down or the last reel stops.
   */
  private visitNet(ctx: GameCtx): { net: number; hands: number } {
    const v = this.visit;
    if (!v) return { net: 0, hands: 0 };
    const s = ctx.slice;
    let net = s.netSum - v.net;
    let hands = s.played - v.played;
    const r = s.result;
    if (r && s.resultSeq > v.seq && !r.aborted && !hasLanded(r, ctx.still, ctx.nowMs, ctx.resultAtMs)) {
      net -= r.yourDelta;
      if (casinoOwnRows(r, s.table).length > 0) hands -= 1;
    }
    return { net, hands: Math.max(0, hands) };
  }

  /**
   * Brings the roulette wheel into view on the edge into SPINNING (R1210).
   *
   * A reader who scrolled down to the felt to bet would otherwise watch
   * the spin off the screen. It runs one time a spin, and it scrolls the
   * box that holds the panel to its very top, because `nearest` stopped
   * with the wheel at the lower edge and the panel head cut off. The
   * overlay keeps the body's scroll position across a rebuild, so the
   * answer stands.
   */
  private followSpin(host: HTMLElement): void {
    const t = this.state.casino.table;
    if (!t || t.game !== CasinoGame.CASINO_GAME_ROULETTE
      || t.phase !== CasinoPhase.CASINO_PHASE_SPINNING) return;
    const key = casinoHandKey(t);
    if (key === this.spunKey) return;
    const wheel = host.querySelector<HTMLElement>('[data-part="wheel"]');
    if (!wheel) return;
    this.spunKey = key;
    // The box that scrolls the wheel: the dock body, the sheet, or the
    // table column of the wide pane (R2198).
    scrollBox(wheel)?.scrollTo?.({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  /**
   * Brings the queue card into view after a QUICK JOIN press.
   *
   * The card stands under the four game cards, so on a phone it appears
   * below the fold, and the reader saw no answer to the press. The server
   * draws the card with its next lobby frame, so the press only marks the
   * wish, and the first render that holds the card scrolls the box down
   * until the card's lower edge shows. A seat that comes before the card
   * cancels the wish, because the table screen replaces the lobby.
   */
  private followQueue(): void {
    const host = this.host;
    if (!this.queueFollow || !host) return;
    if (this.onScreen() !== null) {
      this.queueFollow = false;
      return;
    }
    const card = host.querySelector<HTMLElement>('.queuecard');
    if (!card) return;
    this.queueFollow = false;
    // The box that scrolls the card: the dock body, the sheet, or the
    // board column of the wide pane (R2198).
    const box = scrollBox(card);
    if (!box) return;
    // Chrome px (R1556), the space of the padding and of `scrollTop`.
    const c = chromeRect(card);
    const b = chromeRect(box);
    const floor = b.bottom - (parseFloat(getComputedStyle(box).paddingBottom) || 0);
    // A card taller than the box keeps its head in view.
    const down = Math.min(c.bottom - floor, c.top - b.top);
    if (down <= 0) return;
    box.scrollTo?.({ top: box.scrollTop + down, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  /**
   * Writes the parts whose markup changed, and answers whether it could.
   *
   * False means one part had no anchor in the host — somebody else wrote
   * into it, or the host was rebuilt under the panel — and the caller then
   * writes the whole markup. A patch that stopped half way is no fault,
   * because the rewrite that follows states the same picture.
   */
  private patch(region: HTMLElement, last: ScreenParts, next: ScreenParts): boolean {
    for (const [name, markup] of next.markup) {
      if (last.markup.get(name) === markup) continue;
      const el = region.querySelector(`[data-part="${name}"]`);
      if (!el) return false;
      el.outerHTML = markup;
    }
    return true;
  }

  /** The pack's seven presentation figures, onto the host as properties. */
  private writeClocks(host: HTMLElement): void {
    const s = host.style;
    s.setProperty('--casino-wheel-ms', `${CASINO_WHEEL_MS}ms`);
    s.setProperty('--casino-ball-ms', `${CASINO_BALL_MS}ms`);
    s.setProperty('--casino-reel-ms', `${CASINO_REEL_MS}ms`);
    s.setProperty('--casino-reel-stagger-ms', `${CASINO_REEL_STAGGER_MS}ms`);
    s.setProperty('--casino-timer-fade-ms', `${CASINO_TIMER_FADE_MS}ms`);
    s.setProperty('--casino-flash-ms', `${CASINO_FLASH_MS}ms`);
    // A fraction and not a time: the panel puts the `warn` class on when
    // the turn is inside it, because a stylesheet cannot compare.
    s.setProperty('--casino-timer-warn-frac', `${CASINO_TIMER_WARN_FRAC}`);
  }

  /**
   * Writes the club ink onto the host (R2165, casino.md §16.1): the colour
   * of the club faction on `--club` and `--club-rgb`, which only the
   * identity marks of the sheet read. A change of the club is two writes,
   * and no mark is built again. With no club both go, and the sheet's
   * fallbacks draw the house look.
   */
  private wearClub(host: HTMLElement, fresh: boolean): void {
    const club = clubOf(this.state.casino.lobby);
    if (!fresh && club === this.club) return;
    this.club = club;
    const tokens = clubStyle(club);
    for (const name of CLUB_TOKENS) {
      const v = tokens[name];
      if (v) host.style.setProperty(name, v);
      else host.style.removeProperty(name);
    }
  }

  /** The panel left the screen. The next `sync` writes the host again. */
  reset(): void {
    this.host = null;
    this.wide = false;
    this.club = -1;
    this.regions = [];
    // The reader left the casino, so the visit ends and its net goes.
    this.visit = null;
  }

  /**
   * One release on a casino button, handed over by `Overlay.actButton`.
   *
   * `key` is the arm key of the button, which an armed control passes to
   * `ArmGate.press`. The button's own `data-op` names the act. An op this
   * panel does not own goes to the game screen on the display, which is
   * how a bet, a hit and a raise reach their own module.
   */
  act(btn: HTMLButtonElement, key: string): void {
    const op = btn.dataset.op ?? '';
    const game = Number(btn.dataset.game ?? 0) as CasinoGame;
    const id = Number(btn.dataset.id ?? 0);
    switch (op) {
      // The stake pills of the lobby and of the machine: ONE kept tier
      // serves both (R2165), so the machine opens on the tier the lobby
      // kept. The pick is local, so the panel draws again at once instead
      // of waiting for the next frame of the render loop.
      case 'tier':
        this.tier = Number(btn.dataset.tier ?? 0);
        writeStore(TIER_KEY, String(this.tier));
        this.redraw();
        break;
      // The help fold at the head of a table screen (R2143).
      case 'help': {
        const open = !this.helpOpen(game);
        this.help.set(game, open);
        writeStore(helpKey(game), open ? '1' : '0');
        this.redraw();
        break;
      }
      // The quick queue of one game at one tier (casino.md §5). The server
      // answers with a lobby frame, and with a table frame when it seats
      // the reader.
      case 'quickjoin': {
        // A queue that already holds the reader for this game and tier
        // takes no second join (R2143); the card reads IN QUEUE.
        const lobby = this.state.casino.lobby;
        if (lobby && queuedFor(lobby, game, Number(btn.dataset.tier ?? 0))) break;
        this.machine = null;
        this.queueFollow = true;
        this.actions.casino({
          kind: 'queue-join', game, stakeTier: Number(btn.dataset.tier ?? 0),
        });
        this.followQueue();
        break;
      }
      // The slot machine, which holds no table. The screen opens here and
      // the wire sees nothing until the reader pulls.
      case 'machine':
        this.machine = game;
        this.redraw();
        break;
      case 'queueleave':
        if (btn.dataset.arm === '1' && !this.gate.press(key)) break;
        this.actions.casino({ kind: 'queue-leave' });
        break;
      case 'jointable':
        this.machine = null;
        this.actions.casino({ kind: 'join-table', tableId: id });
        break;
      // LEAVE gives up a seat, so it is armed. At the slot machine it
      // gives up nothing at all — the reader stands at no seat and holds
      // no chips there — so it closes the screen at once.
      case 'leave':
        if (this.state.casino.table) {
          if (btn.dataset.arm === '1' && !this.gate.press(key)) break;
          this.actions.casino({ kind: 'leave' });
          // The seat is given up HERE and not when a frame says so. The
          // server answers a leave with a lobby frame and with no table
          // frame at all, so a panel that waited would draw a dead table
          // until the next lobby cadence. The lobby's own `your_table`
          // clears the slice again if the server refused the leave.
          this.state.clearCasinoTable();
          this.redraw();
          break;
        }
        this.machine = null;
        this.redraw();
        break;
      default: {
        // Every other op belongs to the screen on the display: the
        // roulette bets and SPIN, the blackjack bar, the slot pull, the
        // hold'em bar and TOP UP.
        const view = this.screen();
        if (view) view.act(op, btn, key, this.ctx());
        break;
      }
    }
  }

  /**
   * One casino keybinding (`data/presentation/keybindings.toml`, group
   * `casino`). True means the panel took the press.
   *
   * The press goes to the screen on the display and to nothing else: the
   * seven caps are free on the map surface, but a press must never act on
   * a table the reader does not sit at.
   */
  key(id: MapKeyId): boolean {
    const view = this.screen();
    return view ? view.key(id, this.ctx()) : false;
  }

  /** Draws again now, after a pick that only this panel knows about. */
  private redraw(): void {
    if (this.host) this.sync(this.host);
  }

  /**
   * The game on the display: the game of the table the reader sits at, or
   * the machine the reader opened. Null while the lobby stands.
   */
  private onScreen(): CasinoGame | null {
    const table = this.state.casino.table;
    if (table && casinoIsSeated(table)) return table.game;
    return this.machine;
  }

  /** The screen of that game, while one is registered. */
  private screen(): GameView | undefined {
    const game = this.onScreen();
    return game === null ? undefined : VIEWS[game];
  }

  /**
   * What every screen reads and the two things it may do (`gameview.ts`).
   *
   * One context is built a render pass and a release, so a screen reads
   * one clock reading and one motion answer, and two screens can never
   * disagree about the moment they draw.
   */
  private ctx(): GameCtx {
    return {
      slice: this.state.casino,
      rules: this.state.catalog?.casino ?? null,
      credits: this.state.res.credits,
      nowMs: performance.now(),
      tier: this.tier,
      clockSec: this.state.worldClockSec(),
      still: reducedMotion(),
      resultAtMs: this.resultAt,
      wide: this.wide,
      send: (intent) => this.actions.casino(intent),
      press: (key) => this.gate.press(key),
      armed: (key) => this.gate.is(key),
      armLabel: (key, rest, armFace) => armFaces(rest, armFace, this.gate.is(key)),
    };
  }

  /** The markup of the whole panel, as one string: the dock column and
   *  the sheet. */
  private body(): string {
    const ctx = this.ctx();
    const cas = ctx.slice;
    if (cas.lobby?.closed) return shutHtml();
    const screen = this.screenHtml(ctx);
    if (screen !== '') return screen;
    // The lobby: the net of the visit, the four games, the quick queue and
    // the open tables.
    if (cas.lobby) return this.boardHtml(ctx, cas.lobby, false, false);
    return `<div class="empty">${S.casino_no_table}</div>`;
  }

  /**
   * The two regions of the wide pane (R2198): the board at the left, and
   * the table of the game on the display beside it, or the idle line
   * while no game stands. A seat at a table keeps the board in view, and
   * the board then offers no press that seats the reader a second time.
   */
  private paneBody(): [string, string] {
    const ctx = this.ctx();
    const cas = ctx.slice;
    if (cas.lobby?.closed) return [shutHtml(), ''];
    const screen = this.screenHtml(ctx);
    const table = screen !== '' ? screen : `<div class="cw-idle">${S.casino_pane_idle}</div>`;
    if (!cas.lobby) return [`<div class="empty">${S.casino_no_table}</div>`, table];
    const seated = cas.table !== null && casinoIsSeated(cas.table);
    return [this.boardHtml(ctx, cas.lobby, seated, screen !== ''), table];
  }

  /**
   * The screen of the game on the display, with its help fold and the way
   * off it, or '' while the lobby stands.
   */
  private screenHtml(ctx: GameCtx): string {
    const table = ctx.slice.table;
    if (table && casinoIsSeated(table)) {
      const view = VIEWS[table.game];
      return (view ? this.helpHtml(table.game, table.stakeTier, ctx) + view.html(ctx) : this.seatOnly(table))
        + this.leaveBar(table.id, ctx);
    }
    if (this.machine !== null) {
      const view = VIEWS[this.machine];
      if (view) return this.helpHtml(this.machine, -1, ctx) + view.html(ctx) + this.leaveBar(null, ctx);
    }
    return '';
  }

  /**
   * The board: the net of the visit and the lobby. `seated` turns off every
   * press that seats the reader, and `screen` says that a screen stands
   * beside the board, whose LEAVE row states the visit net in its place.
   */
  private boardHtml(ctx: GameCtx, lobby: CasinoLobby, seated: boolean, screen: boolean): string {
    const { net, hands } = this.visitNet(ctx);
    const visit = screen ? '' : visitLine(net, hands);
    return (visit ? `<div class="casino-visit">${visit}</div>` : '') + lobbyHtml({ ...ctx, lobby, seated });
  }

  /** True while the reader keeps the help fold of `game` open. */
  private helpOpen(game: CasinoGame): boolean {
    let open = this.help.get(game);
    if (open === undefined) {
      open = readStore(helpKey(game)) === '1';
      this.help.set(game, open);
    }
    return open;
  }

  /**
   * The help fold at the head of a table screen (R2143): the facts of
   * that game alone, closed until the reader opens it. `tier` is the stake
   * tier of the table, or -1 at the machine, which states no tier.
   */
  private helpHtml(game: CasinoGame, tier: number, ctx: GameCtx): string {
    return helpPart(game, helpFacts(game, ctx.rules, tier), this.helpOpen(game));
  }

  /**
   * The way off the screen, under every game screen (R988).
   *
   * No game screen draws it: a reader must never hold a seat with no way
   * off it, and the panel owns the seat, so the panel owns the control.
   * At a table LEAVE gives up chips, so it takes two presses (R837) and
   * carries the table's own id as its arm key. At the machine it gives up
   * nothing — the reader holds no seat there — so it is one press and one
   * id of its own.
   */
  private leaveBar(tableId: number | null, ctx: GameCtx): string {
    // The net of this visit stands beside LEAVE, so the reader reads what
    // the visit cost or paid before the press that ends it (R2115).
    const { net, hands } = this.visitNet(ctx);
    const visit = visitLine(net, hands);
    if (tableId === null) {
      return part('leave', 'class="casino-bar leave"',
        visit + ctl('leave', S.casino_leave_machine, true, '', ' data-id="machine"'));
    }
    const armed = this.gate.is(`casino:${tableId}`);
    return part('leave', 'class="casino-bar leave"',
      visit + armedCtl('leave', tableId, armFaces(S.casino_leave_table, S.casino_leave_table_armed, armed),
        armed, 'danger'));
  }

  /**
   * A seat at a table whose screen no module draws.
   *
   * It states where the reader sits and what the last hand paid. The
   * control that takes the reader off the table is `leaveBar` above, which
   * stands under every screen, so this card holds none of its own.
   */
  private seatOnly(table: CasinoTable): string {
    const result = this.state.casino.result;
    const line = result && result.tableId === table.id && result.line !== ''
      ? `<div class="muted">${esc(result.line)}</div>` : '';
    return `<div class="casino-felt"><div class="state">`
      + `${S.casino_you_sit({ id: table.id })}</div>${line}</div>`;
  }
}

/** An operator shut this world's casino (casino.md §9). The switch
 *  dissolves every table and gives every stake back, so the panel states
 *  both facts and offers no control at all. */
function shutHtml(): string {
  return '<div class="casino-shut">'
    + `<div class="shut-head">${S.casino_shut}</div>`
    + `<div class="shut-why">${S.casino_shut_why}</div>`
    + '</div>';
}

/** The stake tier the store holds, or null while it holds none. */
function readTier(): number | null {
  const v = readStore(TIER_KEY);
  if (v === null || v === '') return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/** Takes every child node of `el` off but `keep`. */
function removeAllBut(el: HTMLElement, keep: Node): void {
  for (const n of Array.from(el.childNodes)) if (n !== keep) el.removeChild(n);
}

/** The nearest box above `el` that scrolls its content, if one does. */
function scrollBox(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const y = getComputedStyle(p).overflowY;
    if (y === 'auto' || y === 'scroll') return p;
  }
  return null;
}
